import type { PlanId } from '@/lib/branchTypes'
import type { GraphDatum, LossOfControlBoundsForPlan } from './types'
import trailingActorRiskCdf from './trailingActorRiskCdf.json'

// ─── Trailing ("lagging") actor risk field for the HUD side-panel graph ──────
//
// The black dithered field on the HUD graph ("TRAILING ACTOR RISK") shows how
// far an *unconstrained* project — one racing outside the slowdown deal — has
// probably progressed. It renders the CDF directly: each tile's dot density is
// the actual P(the trailing actor has reached ≥ that height's capability by
// that column's year), via the confidence→density stops (see
// trailingActorRiskShadeField in useExtrapolatedGraphRenderProps).
//
// Source: the `capability_cdf2d_uplift` spreadsheet — a 2D CDF of
// P(an unconstrained project has reached capability ≥ X by year Y | the deal
// survived ≥ N years), baked per survival-slice into trailingActorRiskCdf.json
// (pge = the full CDF grid; capability multipliers match this graph's log
// Y-axis).
//
// There is deliberately NO contour-derived extent and NO capability cap on the
// field: the capability distribution is bimodal (a mover is either still below
// ~20× or has raced past ~400×), so any probability contour leaps that mass
// gap in about a story year, and any hard edge drawn from one — or from a
// hardcoded cap — manufactures a corner that exists nowhere in the data (the
// old P0.35/P0.10 band + 25× frontier cap produced exactly such a square
// elbow). The band below is just the full-column raster region; where P ≈ 0
// the shade is 0 and nothing draws.
//
// The field RECEDES over the scenario: at the current story date the deal has
// survived ≈ (storyYear − dealStart) years, so the shade conditions on that
// many years of survival (survivalYearsForDate). Because a longer-surviving
// deal is strong evidence the trailing actor stayed contained, each higher
// slice thins the field — so as the reader scrolls forward and the deal
// endures, the black field visibly fades. (The field is capped at the current
// story time, so only the past-to-now portion draws.)

export const TRAILING_ACTOR_RISK_DEAL_START_YEAR = trailingActorRiskCdf.dealStartYear

const YEARS = trailingActorRiskCdf.years
export const TRAILING_ACTOR_RISK_YEARS = YEARS
const THRESHOLDS = trailingActorRiskCdf.thresholds
const LOG_THRESHOLDS = THRESHOLDS.map((t) => Math.log(t))
const BY_SURVIVAL = trailingActorRiskCdf.bySurvival as Record<
  string,
  { pge: number[][] }
>
const MIN_SURVIVAL = 1
const MAX_SURVIVAL = 9

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

// P(the trailing actor has reached capability ≥ `capability` by `year`), read
// from the full baked CDF for the given deal-survival slice. This is the raw
// per-capability CONFIDENCE the field's shade should encode — interpolated in
// year and in log-capability across the CDF's threshold grid. Monotonically
// decreasing in capability. Returns 0 outside the covered range.
export function trailingActorRiskConfidenceAt(
  survivalYears: number,
  year: number,
  capability: number,
): number {
  const n = Math.max(MIN_SURVIVAL, Math.min(MAX_SURVIVAL, Math.round(survivalYears)))
  const grid = BY_SURVIVAL[String(n)].pge // [yearIndex][thresholdIndex]
  // Interpolate the CDF over the two threshold-grid rows bracketing `year`.
  const yPos = Math.max(0, Math.min(YEARS.length - 1, year - YEARS[0]))
  const y0 = Math.floor(yPos)
  const y1 = Math.min(YEARS.length - 1, y0 + 1)
  const yt = yPos - y0
  const logCap = Math.log(Math.max(THRESHOLDS[0], capability))
  const pAtRow = (row: number[]): number => {
    if (logCap <= LOG_THRESHOLDS[0]) return row[0]
    const last = LOG_THRESHOLDS.length - 1
    if (logCap >= LOG_THRESHOLDS[last]) return row[last]
    for (let i = 0; i < last; i += 1) {
      if (logCap <= LOG_THRESHOLDS[i + 1]) {
        const span = LOG_THRESHOLDS[i + 1] - LOG_THRESHOLDS[i]
        const t = span > 0 ? (logCap - LOG_THRESHOLDS[i]) / span : 0
        return row[i] + (row[i + 1] - row[i]) * t
      }
    }
    return row[last]
  }
  return clamp01(pAtRow(grid[y0]) + (pAtRow(grid[y1]) - pAtRow(grid[y0])) * yt)
}

// Fast-path evaluator for per-tile shade sampling: the dither raster reads the
// CDF tens of thousands of times per redraw, but the field is separable — the
// year interpolation depends only on the tile's column and the capability
// (threshold-segment) interpolation only on its row. This splits
// trailingActorRiskConfidenceAt into precomputable per-column/per-row params
// (each found once via binary search) plus a few-flops-per-tile combine,
// replacing the per-tile linear scans and Math.log above.
export type TrailingActorRiskYearParams = { y0: number; y1: number; yt: number }
export type TrailingActorRiskCapParams = { i: number; t: number }

export function createTrailingActorRiskConfidenceEvaluator(survivalYears: number): {
  yearParamsFor: (year: number) => TrailingActorRiskYearParams
  capParamsFor: (capability: number) => TrailingActorRiskCapParams
  confidenceAt: (
    yearParams: TrailingActorRiskYearParams,
    capParams: TrailingActorRiskCapParams,
  ) => number
} {
  const n = Math.max(MIN_SURVIVAL, Math.min(MAX_SURVIVAL, Math.round(survivalYears)))
  const grid = BY_SURVIVAL[String(n)].pge // [yearIndex][thresholdIndex]
  const lastThresholdIndex = LOG_THRESHOLDS.length - 1
  return {
    yearParamsFor: (year) => {
      const yPos = Math.max(0, Math.min(YEARS.length - 1, year - YEARS[0]))
      const y0 = Math.floor(yPos)
      return { y0, y1: Math.min(YEARS.length - 1, y0 + 1), yt: yPos - y0 }
    },
    capParamsFor: (capability) => {
      const logCap = Math.log(Math.max(THRESHOLDS[0], capability))
      if (logCap <= LOG_THRESHOLDS[0]) return { i: 0, t: 0 }
      if (logCap >= LOG_THRESHOLDS[lastThresholdIndex]) {
        return { i: lastThresholdIndex - 1, t: 1 }
      }
      let low = 0
      let high = lastThresholdIndex - 1
      while (low < high) {
        const mid = (low + high) >> 1
        if (LOG_THRESHOLDS[mid + 1] < logCap) low = mid + 1
        else high = mid
      }
      const span = LOG_THRESHOLDS[low + 1] - LOG_THRESHOLDS[low]
      return { i: low, t: span > 0 ? (logCap - LOG_THRESHOLDS[low]) / span : 0 }
    },
    confidenceAt: ({ y0, y1, yt }, { i, t }) => {
      const row0 = grid[y0]
      const row1 = grid[y1]
      const p0 = row0[i] + (row0[i + 1] - row0[i]) * t
      const p1 = row1[i] + (row1[i + 1] - row1[i]) * t
      return clamp01(p0 + (p1 - p0) * yt)
    },
  }
}

// Years of deal survival to condition on at a given (possibly fractional) story
// year: the deal lands ~dealStart (the story's ~2030 deal beat), so at storyYear it
// has survived floor(storyYear − dealStart) full years, clamped to the slices
// the CDF covers.
export function survivalYearsForYear(year: number): number {
  const survived = Math.floor(year - TRAILING_ACTOR_RISK_DEAL_START_YEAR)
  return Math.max(MIN_SURVIVAL, Math.min(MAX_SURVIVAL, survived))
}

export function survivalYearsForDate(date: Date): number {
  return survivalYearsForYear(date.getUTCFullYear() + date.getUTCMonth() / 12)
}

// Per-plan time-shift (years) of the shared trailing-actor CDF field, so each
// plan's black field slides independently while every plan renders the SAME
// base field. POSITIVE = LEFT (earlier on the plot), NEGATIVE = RIGHT: the
// shade samples the CDF at (year + shift). Tune these freely.
// C and D always share the same shift — D matches C exactly, by design.
const RACING_CD_SHIFT_YEARS = 9 // 9y left
export const TRAILING_ACTOR_RISK_SHIFT_YEARS_BY_PLAN: Partial<Record<PlanId, number>> = {
  A: 1, // 1y left
  B: 7, // 7y left
  C: RACING_CD_SHIFT_YEARS, // 9y left
  D: RACING_CD_SHIFT_YEARS, // always matches C
  S: -2, // 2y right — field comes in late (~2033); paired with low opacity below
}

// The raster region the CDF shade field draws into: the full plot column,
// floor (1×) to the top of the tabulated capability range. No contours, no
// caps — where the CDF is ~0 the shade is 0 and nothing draws, so the field's
// visible shape comes entirely from the data (see the module comment above).
export function buildTrailingActorRiskBand(): LossOfControlBoundsForPlan {
  const toSeries = (value: number): GraphDatum[] =>
    YEARS.map((y) => ({ date: `${y}-01-01`, value }))
  return {
    min: toSeries(THRESHOLDS[THRESHOLDS.length - 1]),
    max: toSeries(1),
  }
}
